/**
 * Affiliate Wishlist Page
 * Creator's saved products for future requests
 * Includes bulk request functionality
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';
import {
  getCreatorWishlist,
  removeFromWishlist,
  bulkCreateProductRequests,
  WishlistItem,
} from '../../services/affiliateRequests';
import ProductCard from '../../components/affiliate/ProductCard';

export default function AffiliateWishlist() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);

  const [loading, setLoading] = useState(true);
  const [creatorId, setCreatorId] = useState<string | null>(null);
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkRequestDialogOpen, setBulkRequestDialogOpen] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadCreatorProfile();
  }, [user]);

  useEffect(() => {
    if (creatorId) {
      loadWishlist();
    }
  }, [creatorId]);

  const loadCreatorProfile = async () => {
    if (!user) {
      setError('로그인이 필요합니다.');
      setLoading(false);
      return;
    }

    try {

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (sellerError || !sellerProfile) {
        setError('크리에이터 프로필을 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      setCreatorId(sellerProfile.id);
    } catch (err: any) {
      console.error('Load creator profile error:', err);
      setError('프로필을 불러올 수 없습니다: ' + err.message);
      setLoading(false);
    }
  };

  const loadWishlist = async () => {
    if (!creatorId) return;

    try {
      setLoading(true);
      const { data, error: wishlistError } = await getCreatorWishlist(creatorId);

      if (wishlistError) throw wishlistError;

      setWishlist(data || []);
    } catch (err: any) {
      setError('위시리스트를 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (wishlistItemId: string) => {
    try {
      const { error } = await removeFromWishlist(wishlistItemId);
      if (error) throw error;

      setSuccess('위시리스트에서 제거되었습니다.');
      loadWishlist();
    } catch (err: any) {
      setError('제거 실패: ' + err.message);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.size === wishlist.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(wishlist.map((item) => item.id)));
    }
  };

  const handleToggleSelect = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleBulkRequest = async () => {
    if (!creatorId || selectedItems.size === 0) return;

    try {
      // Get product IDs from selected items
      const productIds = wishlist
        .filter((item) => selectedItems.has(item.id))
        .map((item) => item.product_id);

      const { successCount, failedCount, error } = await bulkCreateProductRequests(
        creatorId,
        productIds,
        requestMessage || undefined
      );

      if (error) throw error;

      setSuccess(`${successCount}개 요청 성공, ${failedCount}개 실패`);
      setBulkRequestDialogOpen(false);
      setRequestMessage('');
      setSelectedItems(new Set());
      loadWishlist();
    } catch (err: any) {
      setError('일괄 요청 실패: ' + err.message);
    }
  };

  return (
    <Box sx={styles.pageContainer}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={styles.title}>
          내 위시리스트
        </Typography>

        {wishlist.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<SelectAllIcon />}
              onClick={handleSelectAll}
              sx={{
                borderColor: isDarkMode ? '#fff' : '#000',
                color: isDarkMode ? '#fff' : '#000',
              }}
            >
              {selectedItems.size === wishlist.length ? '전체 해제' : '전체 선택'}
            </Button>
            <Button
              variant="contained"
              startIcon={<ShoppingCartIcon />}
              onClick={() => setBulkRequestDialogOpen(true)}
              disabled={selectedItems.size === 0}
            >
              선택한 상품 요청 ({selectedItems.size})
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : wishlist.length === 0 ? (
        <Paper sx={{ ...styles.paper, p: 8, textAlign: 'center' }}>
          <Typography variant="h6" sx={styles.text}>
            위시리스트가 비어있습니다
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={styles.text}>
            마켓플레이스에서 상품을 추가해보세요
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {wishlist.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <Box sx={{ position: 'relative' }}>
                {/* Selection Checkbox */}
                <Checkbox
                  checked={selectedItems.has(item.id)}
                  onChange={() => handleToggleSelect(item.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 1)',
                    },
                  }}
                />

                {item.product && (
                  <ProductCard
                    productId={item.product_id}
                    productName={item.product.product_name}
                    price={item.product.price}
                    mainImage={item.product.main_images?.[0] || ''}
                    commissionRate={item.product.default_commission_rate}
                    commissionType={item.product.default_commission_type}
                    brandName="브랜드"
                    isInWishlist={true}
                    affiliateStartDate={item.product.affiliate_start_date}
                    affiliateEndDate={item.product.affiliate_end_date}
                    affiliateMaxUses={item.product.affiliate_max_uses}
                    affiliateCurrentUses={item.product.affiliate_current_uses}
                    onRemoveFromWishlist={() => handleRemove(item.id)}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Bulk Request Dialog */}
      <Dialog
        open={bulkRequestDialogOpen}
        onClose={() => setBulkRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.paper }}
      >
        <DialogTitle sx={styles.text}>일괄 접근 요청</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ ...styles.text, mb: 2 }}>
            {selectedItems.size}개 상품에 대한 접근 요청을 보냅니다.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="요청 메시지 (선택사항)"
            placeholder="브랜드에게 보낼 메시지를 입력하세요"
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            sx={styles.input}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRequestDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleBulkRequest}>
            요청 보내기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
